import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';

// Layout Components
import Layout from '@/components/Layout';
import AuthLayout from '@/components/AuthLayout';

// Page Components
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import WorkSessionsPage from '@/pages/WorkSessionsPage';
import ProfilePage from '@/pages/ProfilePage';
import AdminPage from '@/pages/AdminPage';
import UserManagementPage from '@/pages/admin/UserManagementPage';
import SessionApprovalPage from '@/pages/admin/SessionApprovalPage';
import RewardManagementPage from '@/pages/admin/RewardManagementPage';
import PayoutManagementPage from '@/pages/admin/PayoutManagementPage';
import PayoutHistoryPage from '@/pages/admin/PayoutHistoryPage';
import SettingsPage from '@/pages/admin/SettingsPage';
import MonitoringPage from '@/pages/admin/MonitoringPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import ExplorerPage from '@/pages/ExplorerPage';

// Loading Component
import LoadingSpinner from '@/components/LoadingSpinner';

// Route Protection Components
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAuth = true,
  requireAdmin = false 
}) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!requireAuth && isAuthenticated) {
    return <Navigate to="/work-sessions" replace />;
  }

  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/work-sessions" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { checkAuth, isLoading } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading Heperum...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={
          <ProtectedRoute requireAuth={false}>
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/register" element={
          <ProtectedRoute requireAuth={false}>
            <AuthLayout>
              <RegisterPage />
            </AuthLayout>
          </ProtectedRoute>
        } />

        {/* Protected Routes */}
        <Route path="/work-sessions" element={
          <ProtectedRoute>
            <Layout>
              <WorkSessionsPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/explorer" element={
          <ProtectedRoute requireAdmin={true}>
            <Layout>
              <ExplorerPage />
            </Layout>
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin={true}>
            <Layout>
              <AdminPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/admin/users" element={
          <ProtectedRoute requireAdmin={true}>
            <Layout>
              <UserManagementPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/admin/sessions" element={
          <ProtectedRoute requireAdmin={true}>
            <Layout>
              <SessionApprovalPage />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/admin/rewards" element={
          <ProtectedRoute requireAdmin={true}>
            <Layout>
              <RewardManagementPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/admin/payouts" element={
          <ProtectedRoute requireAdmin={true}>
            <Layout>
              <PayoutManagementPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/admin/payouts/history" element={
          <ProtectedRoute requireAdmin={true}>
            <Layout>
              <PayoutHistoryPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/admin/settings" element={
          <ProtectedRoute requireAdmin={true}>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/admin/monitoring" element={
          <ProtectedRoute requireAdmin={true}>
            <Layout>
              <MonitoringPage />
            </Layout>
          </ProtectedRoute>
        } />

        {/* OAuth Callback — must be public, not wrapped in ProtectedRoute */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Default Redirects */}
        <Route path="/" element={<Navigate to="/work-sessions" replace />} />
        <Route path="*" element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
              <p className="text-gray-600 mb-8">Page not found</p>
              <button 
                onClick={() => window.history.back()}
                className="btn-primary"
              >
                Go Back
              </button>
            </div>
          </div>
        } />
      </Routes>
    </div>
  );
};

export default App;